import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Trash2, Loader2 } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { ConfirmAction, fmtDate, Pill } from "./adminUi";

const Th = ({ children, right }) => <th className={`px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>;

const Shell = ({ testid, loading, empty, cols, head, children }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
    <table className="w-full text-left text-sm">
      <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500"><tr>{head}</tr></thead>
      <tbody className="divide-y divide-slate-100">
        {loading && <tr><td colSpan={cols} className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>}
        {!loading && empty && <tr><td colSpan={cols} className="py-10 text-center text-slate-400" data-testid={`${testid}-empty`}>Nothing here yet.</td></tr>}
        {!loading && children}
      </tbody>
    </table>
  </div>
);

const DeleteBtn = ({ id, what, label, onConfirm }) => (
  <ConfirmAction testid={`admin-delete-${id}`} title={`Delete this ${what}?`} description={label}
    confirmLabel="Delete" onConfirm={onConfirm}
    trigger={<Button variant="outline" data-testid={`admin-delete-${id}`} className="h-8 rounded-full px-3 text-xs font-bold text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></Button>} />
);

const useList = (url, key) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get(url)).data[key]); }
    catch (e) { toast({ title: "Could not load", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); }
    finally { setLoading(false); }
  }, [url, key, toast]);
  useEffect(() => { load(); }, [load]);
  const remove = async (delUrl, id) => {
    try { await api.delete(delUrl); setItems((l) => l.filter((x) => x.id !== id)); toast({ title: "Deleted" }); return true; }
    catch (e) { toast({ title: "Delete failed", description: formatApiError(e?.response?.data?.detail), variant: "destructive" }); return false; }
  };
  return { items, loading, remove };
};

export const AdminPosts = ({ type, onChanged }) => {
  const isJob = type === "job";
  const { items, loading, remove } = useList(`/admin/posts?type=${type}`, "posts");
  const del = async (p) => { if (await remove(`/admin/posts/${p.id}`, p.id)) onChanged?.(); };
  return (
    <Shell testid={isJob ? "admin-jobs" : "admin-posts"} loading={loading} empty={!items.length} cols={6}
      head={<><Th>{isJob ? "Job" : "Post"}</Th><Th>By</Th><Th>Community</Th><Th>{isJob ? "Applicants" : "Engagement"}</Th><Th>Posted</Th><Th right>Actions</Th></>}>
      {items.map((p) => (
        <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`admin-post-row-${p.id}`}>
          <td className="max-w-[360px] px-4 py-3">
            <div className="truncate font-bold text-slate-900">{p.title}</div>
            {isJob && p.job && <div className="truncate text-xs text-slate-500">{[p.job.rank, p.job.vessel, p.job.salary].filter(Boolean).join(" · ")}</div>}
          </td>
          <td className="px-4 py-3 text-slate-700">{p.author}</td>
          <td className="px-4 py-3"><Pill>{p.community}</Pill></td>
          <td className="px-4 py-3 text-slate-600">{isJob ? p.applicants : `${p.upvotes} upvotes · ${p.comments} comments`}</td>
          <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtDate(p.created_at)}</td>
          <td className="px-4 py-3 text-right"><DeleteBtn id={p.id} what={isJob ? "job" : "post"} label="Removes it along with its votes, comments and applications." onConfirm={() => del(p)} /></td>
        </tr>
      ))}
    </Shell>
  );
};

export const AdminCommunities = ({ onChanged }) => {
  const { items, loading, remove } = useList("/admin/communities", "communities");
  const del = async (c) => { if (await remove(`/admin/communities/${c.id}`, c.id)) onChanged?.(); };
  return (
    <Shell testid="admin-communities" loading={loading} empty={!items.length} cols={5}
      head={<><Th>Community</Th><Th>Owner</Th><Th>Members</Th><Th>Posts</Th><Th right>Actions</Th></>}>
      {items.map((c) => (
        <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`admin-community-row-${c.id}`}>
          <td className="px-4 py-3"><div className="font-bold text-slate-900">{c.name}</div><div className="text-xs text-slate-500">{c.description}</div></td>
          <td className="px-4 py-3 text-slate-700">{c.owner}</td>
          <td className="px-4 py-3 text-slate-600">{c.member_count}</td>
          <td className="px-4 py-3 text-slate-600">{c.posts}</td>
          <td className="px-4 py-3 text-right"><DeleteBtn id={c.id} what="community" label="Members will no longer see it. Existing posts stay in the feed." onConfirm={() => del(c)} /></td>
        </tr>
      ))}
    </Shell>
  );
};
